/**
 * Wave 29 Chunk 5 — Wellness widget.
 *
 * Renders an emoji-scale mood picker (1-5). Submitting POSTs to
 * /api/wellness/checkin. Shows my 30-day history.
 */
import { useEffect, useState } from "react";
import { Heart, Loader2 } from "lucide-react";
import api from "../../lib/api";
import { toast } from "../ui/sonner";

const FACES = [
  { score: 1, emoji: "😞", label: "Rough" },
  { score: 2, emoji: "😕", label: "Low" },
  { score: 3, emoji: "😐", label: "OK" },
  { score: 4, emoji: "🙂", label: "Good" },
  { score: 5, emoji: "🤩", label: "Great" },
];

export default function WellnessCard() {
  const [picked, setPicked] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/wellness/me");
      setMine(data);
    } catch (_e) { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!picked) return toast.error("Pick a face.");
    setBusy(true);
    try {
      await api.post("/wellness/checkin", { score: picked, note: note.trim() || null });
      toast.success("Check-in saved. Thanks for sharing.");
      setPicked(null); setNote("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="hv-card p-4" data-testid="wellness-card">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] inline-flex items-center gap-1">
          <Heart className="w-3 h-3" /> How are you today?
        </div>
        {mine?.average_30d != null && (
          <div className="text-[10px] text-[#a0aabf]">30-day avg: <span className="text-white tabular-nums">{mine.average_30d}</span></div>
        )}
      </div>
      <div className="flex items-center justify-between gap-1">
        {FACES.map((f) => (
          <button
            key={f.score}
            type="button"
            onClick={() => setPicked(f.score)}
            className={`flex-1 py-2 rounded transition ${picked === f.score ? "bg-[#1FB6C1]/15 ring-2 ring-[#1FB6C1]/60" : "hover:bg-white/5"}`}
            title={f.label}
            data-testid={`wellness-face-${f.score}`}
          >
            <div className="text-2xl">{f.emoji}</div>
            <div className="text-[9px] text-[#647087]">{f.label}</div>
          </button>
        ))}
      </div>
      {picked && (
        <div className="mt-3 space-y-2">
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note (only you can see this)"
            maxLength={500}
            className="hv-input text-xs w-full"
            data-testid="wellness-note"
          />
          <button
            onClick={submit}
            disabled={busy}
            className="hv-btn hv-btn-primary text-xs w-full disabled:opacity-50"
            data-testid="wellness-submit"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Heart className="w-3.5 h-3.5" />} Save check-in
          </button>
        </div>
      )}
      {mine?.items?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-white/5">
          <div className="text-[10px] text-[#647087] mb-1">Last 30 days</div>
          <div className="flex items-center gap-1 flex-wrap">
            {mine.items.slice(0, 14).map((r) => {
              const f = FACES.find((x) => x.score === r.score) || FACES[2];
              return (
                <span key={r.id} className="text-base" title={`${new Date(r.at).toLocaleDateString("en-ZA")} · ${f.label}${r.note ? ` · ${r.note}` : ""}`}>
                  {f.emoji}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
